import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { connectionTargets, referenceTargets } from '../connectedBible'
import type { ReferenceTarget, StoryProject } from '../types'

export function ConnectionPicker({ project, sourceId, value, onChange, onQueryChange }: {
  project: StoryProject
  sourceId: string
  value: string
  onChange: (id: string) => void
  onQueryChange: (query: string) => void
}) {
  const id = useId(), input = useRef<HTMLInputElement>(null), options = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState(''), [open, setOpen] = useState(false), [active, setActive] = useState(0), [limit, setLimit] = useState(50)
  const selected = referenceTargets(project).find(target => target.id === value && target.id !== sourceId)
  const matches = connectionTargets(project, sourceId, value ? '' : query)
  const shown = matches.slice(0, limit), index = Math.min(active, Math.max(0, shown.length - 1))
  const choose = (target: ReferenceTarget) => {
    input.current?.focus({ preventScroll: true })
    onChange(target.id); setQuery(''); onQueryChange(''); setOpen(false)
  }
  useLayoutEffect(() => {
    if (open) options.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [open, index])
  // The parent clears value after connecting. A previous search must not look
  // like a still-selected entry, including after a live deletion.
  return <div className="connection-picker" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
  }}>
    <label htmlFor={id}>Connect to</label>
    <div className="connection-target-control">
      <Search size={15} aria-hidden="true" />
      <input ref={input} id={id} role="combobox" aria-autocomplete="list" aria-expanded={open}
        aria-controls={open ? id + '-options' : undefined} aria-activedescendant={open && shown.length ? id + '-option-' + index : undefined}
        autoComplete="off" placeholder="Search people, places, scenes…" value={selected?.title || query}
        onFocus={() => { setOpen(true); setActive(0) }}
        onChange={event => { const text = event.target.value; setQuery(text); onQueryChange(text); onChange(''); setOpen(true); setActive(0); setLimit(50) }}
        onKeyDown={event => {
          if (event.nativeEvent.isComposing) return
          if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false) }
          else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); event.stopPropagation()
            setOpen(true); setActive(open ? (index + (event.key === 'ArrowDown' ? 1 : -1) + Math.max(1, shown.length)) % Math.max(1, shown.length) : 0)
          } else if (event.key === 'Enter' && open) {
            event.preventDefault(); event.stopPropagation()
            if (shown[index]) choose(shown[index])
          } else if (event.key === 'Tab') setOpen(false)
        }} />
      {(value || query) && <button type="button" className="icon-button" aria-label="Clear connection selection" onClick={() => {
        onChange(''); setQuery(''); onQueryChange(''); setActive(0); setLimit(50); setOpen(true); input.current?.focus({ preventScroll: true })
      }}><X size={14} /></button>}
    </div>
    {open && <div className="connection-picker-results">
      <div ref={options} id={id + '-options'} role="listbox" aria-label="Connection options" className="connection-options">
        {shown.map((target, position) => <button key={target.id} id={id + '-option-' + position} type="button" role="option" tabIndex={-1}
          aria-selected={target.id === value} data-active={position === index} onMouseDown={event => event.preventDefault()}
          onClick={() => choose(target)}><span>{target.title}</span><small>{target.section}</small></button>)}
      </div>
      <p role="status">{matches.length ? matches.length + (matches.length === 1 ? ' match' : ' matches') : 'No matching entries or scenes. Try another name or section.'}</p>
      {matches.length > shown.length && <button type="button" className="text-button" onClick={() => setLimit(count => count + 50)}>Show more results</button>}
    </div>}
  </div>
}
